import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Stack,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import type { BulkOperationResultsProps } from './types';

export const BulkOperationResults: React.FC<BulkOperationResultsProps> = ({
  operationResult,
  showResultDetails,
  onToggleDetails,
}) => {
  return (
    <Box>
      {/* Status Alert */}
      <Alert
        severity={
          operationResult.status === 'success'
            ? 'success'
            : operationResult.status === 'partial_success'
              ? 'warning'
              : 'error'
        }
        icon={
          operationResult.status === 'success' ? (
            <CheckCircleIcon />
          ) : operationResult.status === 'partial_success' ? (
            <WarningIcon />
          ) : (
            <ErrorIcon />
          )
        }
        sx={{ mb: 2 }}
      >
        <Typography variant="body1" fontWeight={500}>
          {operationResult.status === 'success' &&
            ((operationResult.summary.skipped ?? 0) > 0
              ? `Updated ${operationResult.summary.succeeded} store${operationResult.summary.succeeded !== 1 ? 's' : ''} successfully. ${operationResult.summary.skipped} skipped.`
              : 'All stores updated successfully!')}
          {operationResult.status === 'partial_success' && (
            <>
              Updated {operationResult.summary.succeeded}/{operationResult.summary.total} stores.
              {operationResult.summary.failed > 0 && ` ${operationResult.summary.failed} failed.`}
              {(operationResult.summary.skipped ?? 0) > 0 &&
                ` ${operationResult.summary.skipped} skipped.`}
            </>
          )}
          {operationResult.status === 'failure' && 'Failed to update stores.'}
        </Typography>
      </Alert>

      {/* Summary Stats */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Stack direction="row" spacing={3} justifyContent="center">
          <Box textAlign="center">
            <Typography variant="h5" color="success.main" fontWeight={600}>
              {operationResult.summary.succeeded}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Succeeded
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h5" color="error.main" fontWeight={600}>
              {operationResult.summary.failed}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Failed
            </Typography>
          </Box>
          {operationResult.summary.retryable > 0 && (
            <Box textAlign="center">
              <Typography variant="h5" color="warning.main" fontWeight={600}>
                {operationResult.summary.retryable}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Retryable
              </Typography>
            </Box>
          )}
          {(operationResult.summary.skipped ?? 0) > 0 && (
            <Box textAlign="center">
              <Typography variant="h5" color="text.secondary" fontWeight={600}>
                {operationResult.summary.skipped}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Skipped
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Failed Stores Details (Expandable) */}
      {operationResult.failedStoreNumbers.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={onToggleDetails}
          >
            <Typography variant="subtitle2">
              Failed Stores ({operationResult.failedStoreNumbers.length})
            </Typography>
            <IconButton size="small">
              {showResultDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={showResultDetails}>
            <List dense sx={{ mt: 1 }}>
              {operationResult.results
                .filter((r) => !r.success)
                .map((result) => (
                  <ListItem key={result.storeNumber} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {result.error?.category === 'TRANSIENT' ? (
                        <WarningIcon color="warning" fontSize="small" />
                      ) : (
                        <ErrorIcon color="error" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          Store {result.storeNumber}
                          {result.error?.category === 'TRANSIENT' && (
                            <Chip
                              label="Retryable"
                              size="small"
                              color="warning"
                              sx={{ ml: 1, height: 20 }}
                            />
                          )}
                        </Typography>
                      }
                      secondary={result.error?.message}
                    />
                  </ListItem>
                ))}
            </List>
          </Collapse>
        </Paper>
      )}

      {/* Skipped Stores Info */}
      {operationResult.skippedStoreNumbers && operationResult.skippedStoreNumbers.length > 0 && (
        <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Skipped Stores ({operationResult.skippedStoreNumbers.length})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {operationResult.skipReason || 'Channel not configured for these stores'}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
            Stores: {operationResult.skippedStoreNumbers.join(', ')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
